/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { DefaultTable } from 'src/components';
import { useTranslation } from 'react-i18next';
import editIcon from 'src/assets/edit.svg';
import LeavepageService from 'src/services/leave-page.service';
import Chip from './chip';
import { minHeight } from '@mui/system';

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 16px Biotif',
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '0px',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
      minHeight: '40px',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  cells: {
    style: {
      justifyContent: 'left',
      '&:not(:nth-of-type(2))': {
        justifyContent: 'left',
      },
      '&:first-of-type': {
        justifyContent: 'left',
      },
      '&:last-of-type': {
        justifyContent: 'left',
      },
    },
  },
  pagination: {
    style: {
      border: 'none',
      svg: {
        fill: 'rgba(0,0,0,0.54)',
      },
    },
  },
};

const EmployeeTable: React.FC<{ onEdit: (leaveData: any) => void }> = ({ onEdit }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaveRecords = async () => {
    setLoading(true);
    try {
      const leaveData = await LeavepageService.getLeaveRecords();
      setData(leaveData.map((record: { attachment: any }) => ({ ...record, attachment: record.attachment || null })));
    } catch (error) {
      console.error('Error fetching leave records:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveRecords();
  }, []);

  const handleEditClick = async (row: any) => {
    if (typeof onEdit !== 'function') {
      console.error('onEdit is not a function');
      return;
    }

    try {
      onEdit({
        ...row,
        attachment: row.attachment || null,
      });
    } catch (error) {
      console.error('Error handling edit click:', error);
    }
  };

  const columns = [
    {
      name: 'Leave Type',
      selector: (row: any) => row.accrualName || '',
      width: '150px',
    },
    {
      name: 'From Date',
      selector: (row: any) => row.startDate || '',
      width: '150px',
    },
    {
      name: 'To Date',
      selector: (row: any) => row.endDate || '',
      width: '150px',
    },
    {
      name: 'Days',
      selector: (row: any) => row.length || '',
      width: '150px',
    },

    {
      name: 'Attachments',
      selector: (row: any) => {
        const atts = row.attachments || row.attachment || [];
        console.log('✅ Row attachments:', row.attachments, row.attachment);
        return Array.isArray(atts) ? atts.length : atts ? 1 : 0;
      },
      width: '250px',
      cell: (row: any) => {
        let attachments = row.attachments || row.attachment || [];

        if (!Array.isArray(attachments) && attachments) {
          attachments = [attachments];
        }

        if (!attachments.length) {
          return <span style={{ color: '#888', fontSize: '12px' }}>No attachments</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {attachments.map((att: any, idx: number) => {
              const url = att.attachmentUrl || att.url || att.fileUrl || att.link || att.href || att.downloadUrl;
              const name =
                att.attachmentName || att.name || att.filename || att.originalName || `Attachment ${idx + 1}`;

              return (
                <a
                  key={idx}
                  href={url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#4A4BA9',
                    textDecoration: 'none',
                    fontSize: '14px',
                    padding: '2px 0',
                    display: 'block',
                  }}
                >
                  📎 {name}
                </a>
              );
            })}
          </div>
        );
      },
    },
    {
      name: 'Reason',
      selector: (row: any) => row.reason || '',
      width: '220px',
      cell: (row: any) => (
        <div
          title={row.reason || ''}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '220px' }}
        >
          {row.reason || ''}
        </div>
      ),
    },
    {
      name: 'Status',
      selector: (row: any) => row.status || '',
      width: '150px',
      cell: (row: any) => {
        const statusColors: Record<string, string> = {
          Approved: '#4CAF50',
          Pending: '#FF9800',
          Rejected: '#F44336',
          Canceled: '#9E9E9E',
        };
        const color = statusColors[row.status] || '#BDBDBD';
        return <Chip label={row.status} color={color} />;
      },
    },
    {
      name: 'Action',
      selector: (row: any) => row.accrualName || 'N/A',
      width: '120px',
      cell: (row: any) => {
        if (row.status === 'Withdrawn' || row.status === 'Approved' || row.status === 'Rejected') {
          return null;
        }
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={() => handleEditClick(row)}
          >
            <img src={editIcon} alt="Edit Icon" style={{ width: '20px', height: '20px' }} />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <DefaultTable columns={columns} data={data || []} customStyles={customStyles} />
      )}
    </div>
  );
};

export default EmployeeTable;
